// Linked deals page data (linkedDealsAction). Confirm / dismiss go through
// decideDealLoanLink (decideAwaitingPayment in useAwaitingPayment.ts).
import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { tenantHasAggregatorFeature } from '@valutten/shared-schemas/dist/aggregatorProfiles';
import { functions } from '../../lib/firebase';

/**
 * Client mirror of linkedDealsAction's gate: the tenant has the Linked deals
 * aggregator feature, and the user is a company-wide confirmer (super_admin,
 * or an administrator who sees all offices), with no entity or loan-book
 * restriction. The server enforces the same again. Founder view-as arrives as
 * super_admin with all offices.
 */
export function canUseLinkedDeals(
  user: Record<string, unknown> | null | undefined,
  company: { aggregator?: unknown; featureFlags?: Record<string, unknown> } | null | undefined,
): boolean {
  if (!user || !tenantHasAggregatorFeature(company ?? null, 'linked_deals')) return false;
  const nonEmpty = (v: unknown) => Array.isArray(v) && v.length > 0;
  if (nonEmpty(user.allowedEntityCodes) || nonEmpty(user.allowedLoanBookIds) || Array.isArray(user.allowedTemplateIds)) return false;
  if (user.role === 'super_admin') return true;
  return user.role === 'administrator' && user.canSeeAllOffices === true;
}

export interface LinkedLoanView {
  loanId: string;
  customerName: string | null;
  lender: string | null;
  settlementDate: string | null;
  loanAmountCents: number | null;
  active: boolean;
}

export interface LinkedDealRow {
  dealId: string;
  borrowerName: string | null;
  lender: string | null;
  settledDate: string | null;
  dealAmountCents: number | null;
  loans: LinkedLoanView[];
  anyActive: boolean;
}

export interface SampleRow extends LinkedDealRow {
  status: 'pending' | 'correct' | 'wrong';
}

export interface SampleSummary {
  size: number;
  reviewed: number;
  wrong: number;
  errorRateUpper95: number | null;
}

export interface LinkedDealsOverview {
  byState: Record<string, { deals: number; activeDeals: number }>;
}

export interface ToConfirmPage {
  total: number;
  offset: number;
  deals: LinkedDealRow[];
}

export interface SampleResponse {
  /** null until the sample is drawn. */
  summary: SampleSummary | null;
  deals: SampleRow[];
  /** too_few = fewer automatic links than the sample needs; stale = a stored sample this version cannot use. */
  notReady: 'too_few' | 'stale' | null;
  autoLinks: number | null;
  needed: number;
}

type Req =
  | { action: 'overview'; companyId: string }
  | { action: 'listToConfirm'; companyId: string; offset: number; limit: number; activeOnly: boolean }
  | { action: 'sample'; companyId: string };

function call<Res>(req: Req): Promise<Res> {
  return httpsCallable<Req, Res>(functions, 'linkedDealsAction')(req).then((r) => r.data);
}

function useCall<Res>(req: Req | null, tick: number) {
  const [data, setData] = useState<Res | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const key = req ? JSON.stringify(req) : '';
  useEffect(() => {
    if (!req) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    call<Res>(req)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: { message?: string }) => {
        console.error('[useLinkedDeals]', err);
        if (!cancelled) setError(err?.message || 'Failed to load linked deals');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick]);
  return { data, loading, error };
}

export function useLinkedDealsOverview(companyId: string | null, tick: number) {
  return useCall<LinkedDealsOverview>(companyId ? { action: 'overview', companyId } : null, tick);
}

export function useLinkedDealsToConfirm(companyId: string | null, offset: number, limit: number, activeOnly: boolean, tick: number) {
  return useCall<ToConfirmPage>(companyId ? { action: 'listToConfirm', companyId, offset, limit, activeOnly } : null, tick);
}

export function useLinkedDealsSample(companyId: string | null, tick: number) {
  return useCall<SampleResponse>(companyId ? { action: 'sample', companyId } : null, tick);
}
